import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IAcquireSources,
  IEmployeeCompetency,
  IEmployeeCompetencyInput,
} from "models/employeeCompetencies";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import BlockIcon from "@mui/icons-material/Block";
import {
  EmployeeCompetencyCells,
  addEmployeeCompetency,
  deleteEmployeeCompetency,
  editEmployeeCompetency,
  getCompetenciesAcquireSources,
  getEmployeeCompetencies,
} from "framework/employeeCompetencies";

import Search from "components/Search/Search";
import AddEmployeeCompetency from "./partials/AddEmployeeCompetency/AddEmployeeCompetency";
import EditEmployeeCompetency from "./partials/EditEmployeeCompetency/EditEmployeeCompetency";
import DeleteEmployeeCompetency from "./partials/DeleteEmployeeCompetency/DeleteEmployeeCompetency";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission, getUserData } from "helpers";
import { getCompetencies } from "framework/competencies";
import { ICompetency } from "models/competency";
import { useParams } from "react-router-dom";
import { getCompanyCompetencies } from "framework/companyCompetencies";

interface Props {
  feature: Feature;
}

const EmployeeCompetencies: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [loading4, setLoading4] = useState<boolean>(true);
  const user = getUserData();
  const { colors } = user;
  const { t, i18n } = useTranslation();
  const [EmployeeCompetencies, setEmployeeCompetencies] = useState<
    IEmployeeCompetency[]
  >([] as IEmployeeCompetency[]);
  const [FilterEmployeeCompetencies, setFilterEmployeeCompetencies] = useState<
    IEmployeeCompetency[]
  >([] as IEmployeeCompetency[]);
  const [Competencies, setCompetencies] = useState<ICompetency[]>(
    [] as ICompetency[]
  );
  const [AcquireSources, setAcquireSources] = useState<IAcquireSources[]>(
    [] as IAcquireSources[]
  );

  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedEmployeeCompetency, setSelectedEmployeeCompetency] =
    useState<IEmployeeCompetency>({} as IEmployeeCompetency);
  const [inputObj, setInputObj] = useState<IEmployeeCompetencyInput>(
    {} as IEmployeeCompetencyInput
  );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();
  const urlParams = useParams();
  const employeeId: string = urlParams[`${feature.singleName}Id`] as string;
  const addEmployeeCompetenciesMutation = useMutation({
    mutationFn: (createInput: IEmployeeCompetencyInput) => {
      return addEmployeeCompetency(createInput);
    },
  });

  const editEmployeeCompetenciesMutation = useMutation({
    mutationFn: (data: {
      createInput: IEmployeeCompetencyInput;
      id: string;
    }) => {
      return editEmployeeCompetency(data.createInput, data.id);
    },
  });

  const deleteEmployeeCompetenciesMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteEmployeeCompetency(code);
    },
  });

  const fetchCompetencies = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get company competencies", () =>
        getCompanyCompetencies()
      );
      setCompetencies(response);
      setLoading4(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading4(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchAcquireSources = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery(
        "get acquire sources",
        () => getCompetenciesAcquireSources()
      );
      setAcquireSources(response);
      setLoading4(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading4(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchData = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery(
        "get EmployeeCompetencies",
        () => getEmployeeCompetencies(`?employeeId=${employeeId}`)
      );

      setEmployeeCompetencies(
        response.map((item: IEmployeeCompetency) => ({
          ...item,
        }))
      );
      setFilterEmployeeCompetencies(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    try {
      const res = await getPermission(3);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchCompetencies();
    fetchData();
    fetchPermission();
    fetchAcquireSources();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3 || loading4)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name == "verified" && value == null) {
      value = false;
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (EmployeeCompetency: IEmployeeCompetency) => {
    setSelectedEmployeeCompetency(EmployeeCompetency);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedEmployeeCompetency({} as IEmployeeCompetency);
  };

  const handleOpenDelete = (EmployeeCompetency: IEmployeeCompetency) => {
    setSelectedEmployeeCompetency(EmployeeCompetency);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedEmployeeCompetency({} as IEmployeeCompetency);
  };

  const getBodyCells = (row: IEmployeeCompetency) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "competencyName")
        return {
          id: "competencyName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.competencyName}</Typography>
            </TableCell>
          ),
        };
      if (key === "acquireSource")
        return {
          id: "acquireSource",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row?.acquireSourceDescription}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "verified")
        return {
          id: "verified",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row?.verified ? "Yes" : "No"}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.competencyId || !inputObj.acquireSource)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);
      inputObj.employeeId = employeeId;
      await addEmployeeCompetenciesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Employee competency added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    if (
      !inputObj.competencyId &&
      !inputObj.acquireSource &&
      !("verified" in inputObj)
    )
      return setAlert({
        open: true,
        message: "No changes detected",
        type: "error",
      });
    try {
      setLoading2(true);
      let createInput: IEmployeeCompetencyInput = {
        competencyId:
          inputObj.competencyId || selectedEmployeeCompetency.competencyId,
        acquireSource:
          inputObj.acquireSource || selectedEmployeeCompetency.acquireSource,
        employeeId: employeeId,
        verified: !("verified" in inputObj)
          ? selectedEmployeeCompetency.verified
          : inputObj.verified,
      };
      await editEmployeeCompetenciesMutation.mutateAsync({
        createInput,
        id: selectedEmployeeCompetency.id,
      });
      setAlert({
        open: true,
        message: "Employee competency updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteEmployeeCompetenciesMutation.mutateAsync(
        selectedEmployeeCompetency.id
      );
      setAlert({
        open: true,
        message: "Employee competency deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterEmployeeCompetencies(EmployeeCompetencies);
    const search = EmployeeCompetencies.filter((item) =>
      item.competencyName.toLowerCase().includes(value.toLowerCase())
    );
    setFilterEmployeeCompetencies(search);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! &&
          FilterEmployeeCompetencies.length > 0 && (
            <List
              headCells={EmployeeCompetencyCells}
              data={FilterEmployeeCompetencies}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterEmployeeCompetencies ||
          FilterEmployeeCompetencies.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_employee_competency_header")}
              message={t("T_no_employee_competency_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddEmployeeCompetency
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          Competencies={Competencies}
          AcquireSources={AcquireSources}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditEmployeeCompetency
          open={openEdit}
          loading={loading2}
          selectedEmployeeCompetency={selectedEmployeeCompetency}
          Competencies={Competencies}
          AcquireSources={AcquireSources}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteEmployeeCompetency
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default EmployeeCompetencies;
